export interface Volume {
  number: string
  title: string
  subtitle: string
  intro: string[]
  questions?: string[]
  highlight?: string
  afterHighlight?: string
  bulletsIntro: string
  bullets: string[]
  sequenceIntro?: string
  sequence?: string
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  resultLead?: string
  resultQuote?: string
  result: string
  resultEmphasis?: string
  popular?: boolean
  popularLabel?: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume 01',
    title: 'Sewing Fundamentals, Tools, Fabrics & Machine Techniques',
    subtitle: 'The Foundation Every Sewing Student Needs',
    intro: [
      'Every new group starts with the same challenge: tools, fabrics, threading, tension, stitch settings and the first straight seam, all before the first real project.',
      'The basics look simple, but students only get them right when they can see every part, every setting and every common mistake.',
      'This volume turns sewing fundamentals into labeled diagrams, settings tables, step-by-step sequences, ❌/✅ comparisons, teaching cues and studio drills you can use from the very first class.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'The sewing studio setup',
      'Essential hand tools',
      'Measuring & marking tools',
      'Pressing equipment',
      'Parts of the sewing machine',
      'Threading the machine & winding the bobbin',
      'Needles: types & sizes (60/8 to 110/18)',
      'Thread tension basics',
      'Stitch length & width settings',
      'Machine care & troubleshooting',
      'Natural vs. synthetic fibers',
      'Woven vs. knit fabrics',
      'Fabric grain & selvage',
      'Essential hand stitches',
      'Seam types & seam finishes',
      '4-week fundamentals plan & self-check',
    ],
    result: 'Give every student confident control of the machine, the tools and the fabric before their first garment.',
  },
  {
    number: 'Volume 02',
    title: 'Pattern Making, Body Measurements & Garment Drafting',
    subtitle: 'Measure It, Draft It, Test It',
    intro: [
      'Pattern making is where students feel most unsure: measurements to take, ease to add, symbols to read and blocks with too many steps to remember.',
      'Without a systematic method, a small measuring mistake turns into a garment that does not fit.',
      'This volume organizes measuring and drafting into body measurement maps, symbol charts, step-by-step block drafts with worked numbers, toile checks and classroom drills.',
    ],
    popular: true,
    popularLabel: 'CORE DRAFTING',
    bulletsIntro: 'Inside:',
    bullets: [
      'Pattern-making tools',
      'Body measurement map',
      'Measuring bust, waist & hip',
      'Measuring lengths, shoulders & arms',
      'Measuring for trousers',
      'Ease explained',
      'Sizing systems & size charts',
      'Pattern symbols & labeling',
      'Adding seam allowance step by step',
      'What is a block / sloper?',
      'Drafting the basic skirt block',
      'Drafting the bodice block',
      'Drafting the basic sleeve block',
      'Drafting the basic trouser block',
      'Testing the block in muslin (toile)',
      '4-week drafting plan & self-check',
    ],
    result: 'Help students measure accurately and draft clean, well-balanced blocks with a clear, repeatable method.',
  },
  {
    number: 'Volume 03',
    title: 'Advanced Pattern Making, Draping & Garment Fitting',
    subtitle: 'From Block to Design, From Toile to Fit',
    intro: [
      'Turning a basic block into a real design demands precision: moving darts, adding style lines, shaping collars and solving fit problems on the body.',
      'Explaining slash-and-spread, draping or a full bust adjustment is hard until students can see every step side by side.',
      'This volume covers design patterns, draping and fitting with numbered sequences, before-and-after diagrams, wrinkle-reading guides, ❌/✅ cards and dress-form practice.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'From block to design pattern',
      'Dart manipulation: slash-and-spread & pivot',
      'Princess seams, gathers, tucks & pleats',
      'Yokes & necklines',
      'Flat, stand, shirt & shawl collars',
      'Sleeve variations, cuffs & plackets',
      'Skirt & trouser variations',
      'Facings & linings patterns',
      'Preparing the dress form & muslin',
      'Draping a basic bodice & skirt',
      'Draping a cowl neckline',
      'Reading wrinkles & the fitting sequence',
      'Full & small bust adjustments',
      'Shoulder, back, waist & hip adjustments',
      'Grading increments & methods',
      'Technical flats & pattern development',
    ],
    result: 'Help students turn blocks into designs and fix fit problems with precise, consistent technique.',
  },
  {
    number: 'Volume 04',
    title: 'Garment Cutting, Assembly & Construction Techniques',
    subtitle: 'Cut Right, Sew in Order, Finish Clean',
    intro: [
      'Garment construction is where good patterns succeed or fail, and the construction order is where students get lost most often.',
      'Layouts, interfacing, collars, sleeves, waistbands, pockets and zippers are hard to teach without clear visuals and exact measurements.',
      'This volume explains cutting and construction with layout diagrams, step-by-step sequences, exact stitching distances, ❌/✅ comparisons and practice samples.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'The construction order',
      'Fabric layout rules & yardage',
      'Matching plaids & stripes',
      'Cutting & marking techniques',
      'Interfacing & stay-stitching',
      'Sewing darts',
      'Facings & bias binding',
      'Flat collar & shirt collar with stand',
      'Set-in & flat-method sleeves',
      'Straight, elastic & contoured waistbands',
      'Patch, in-seam, slant & welt pockets',
      'Centered, lapped, invisible & fly-front zippers',
      'Buttonholes, buttons, snaps, hooks & eyes',
      'Hemming by hand and by machine',
      'Constructing a skirt, a shirt & trousers',
      'Construction troubleshooting',
    ],
    result: 'Help students cut accurately and assemble every garment in the right order with clean, professional results.',
  },
  {
    number: 'Volume 05',
    title: 'Professional Tailoring, Finishing Techniques & Garment Alterations',
    subtitle: 'The Details That Make It Professional',
    intro: [
      'Tailoring, finishing and alterations are where students move from homemade to professional: linings, canvas, pad stitching, pressing and invisible repairs.',
      'These skills are hard to teach with words alone; students need to see the inside of a well-made garment.',
      'This volume explains tailoring, finishing and alterations with labeled cross-sections, step-by-step sequences, pressing guides, quality checklists and real alteration workflows.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'What makes a garment professional',
      'Bound & Hong Kong seams',
      'Hem types & hand-finished hems',
      'Bagging a lining & lining a skirt',
      'Full canvas vs. half canvas vs. fused',
      'Pad stitching & taping the roll line',
      'Tailored collar & lapel construction',
      'Tailored sleeves, vents & pockets',
      'Hand-worked & keyhole buttonholes',
      'The art of pressing & shaping with steam',
      'Alteration assessment & pinning',
      'Shortening trousers & the original jeans hem',
      'Taking in waists & side seams',
      'Replacing a zipper & jacket alterations',
      'Delicate, heavy, knit & leather fabrics',
      'Quality control checklists',
    ],
    result: 'Help students finish, tailor and alter garments with the clean details of professional work.',
  },
  {
    number: 'Volume 06',
    title: 'Sewing Instruction, Teaching Programs & Practical Assessments',
    subtitle: 'Teach With a System',
    intro: [
      'Knowing how to sew is not the same as knowing how to teach it.',
      'Sewing instructors need clear methods for lessons, demonstrations, mixed-level classes, feedback at the machine, course programs and practical assessment.',
      'This volume gives you lesson structures, demonstration guides, project banks, skill drills, course programs, competency checklists, rubrics and certificates ready to use.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'The role of the sewing instructor',
      'How adults learn practical skills',
      'Skill progression framework',
      'Designing a sewing curriculum & learning outcomes',
      'Lesson plan template & sample lesson plans',
      'The 4-step demonstration method',
      'Demonstrations everyone can see & sample boards',
      'Managing a mixed-level class',
      'Classroom setup & studio safety briefing',
      'Beginner, intermediate & advanced project banks',
      'Skill drills: machine control, seams, drafting & fitting',
      'Giving feedback at the machine',
      '8-week, 12-week & pattern-making course programs',
      'Practical assessments & scoring rubrics',
      'Competency checklists: beginner to advanced',
      'Portfolios, progress trackers & certificates',
    ],
    result: 'Plan, deliver and assess every lesson, workshop and course with structure, from first-time beginners to advanced students.',
  },
]
